import { FC, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'src/global.css';
import 'src/locales/i18n';
import { RTL } from 'src/components/rtl';
import { SettingsDrawer } from 'src/components/settings/settings-drawer';
import { Toaster } from 'src/components/toaster';
import { SettingsConsumer, SettingsProvider } from 'src/contexts/settings';
import { useNprogress } from 'src/hooks/use-nprogress';
import { createTheme } from 'src/theme';
import Router from './routes/routes';
import { StopwatchProvider } from './layouts/check-in-checkout/StopwatchContext';
import { paths } from './paths';
import DataDogRum from './components/dataDogRum/DataDogRum';
import AuthContext from './routes/AuthContext';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { resetTherapistProfile } from './redux/actions/facilityActions';
import { localhostSplits } from './localhostSplits';
import { splitIoAuthorizationKey } from './config';
import { TherapyPlanningProvider } from './components/therapyPlanning_2.0/therapyPlanningProvider';

export const App: FC = () => {
  useNprogress();
  const { landingPage } = paths;
  const hostName = window.location.hostname;
  const pathName = window.location.pathname;
  const auth = useContext(AuthContext);
  const isAuthenticated = auth.authenticated;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityId = localStorage.getItem('facilityId') || '';

  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      // Setting authorizationKey to 'localhost' for localhost development
      // You can learn more here: https://help.split.io/hc/en-us/articles/360038825091-React-SDK#localhost-mode
      // authorizationKey: hostName === 'localhost' ? 'localhost' : import.meta.env.VITE_SPLIT_IO_KEY,
      authorizationKey: splitIoAuthorizationKey,
      key: facilityId,
    },
    // Mocking features/splits if running in localhost
  //  features: hostName === 'localhost' ? localhostSplits : undefined,
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (hostName === 'localhost' && auth.realm === pathName.split('/')[1]) {
      navigate(landingPage);
      return;
    }

    if (hostName !== 'localhost' && pathName === '/') {
      navigate(landingPage);
      return;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) return;
    dispatch(resetTherapistProfile());
  }, [isAuthenticated]);

  return isAuthenticated ? (
    <SplitFactoryProvider config={sdkConfig}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SettingsProvider>
          <SettingsConsumer>
            {(settings) => {
              if (!settings.isInitialized) {
                // return null;
              }
              const theme = createTheme({
                colorPreset: 'indigo',
                contrast: 'normal',
                direction: 'ltr',
                paletteMode: 'light',
                responsiveFontSizes: settings.responsiveFontSizes,
              });

              return (
                <ThemeProvider theme={theme}>
                  <Helmet>
                    <meta
                      name="color-scheme"
                      content={'light'}
                    />
                    <meta
                      name="theme-color"
                      content={theme.palette.neutral[900]}
                    />
                  </Helmet>
                  <RTL direction={'ltr'}>
                    <CssBaseline />

                    <StopwatchProvider>
                      <TherapyPlanningProvider>
                        <Router />
                      </TherapyPlanningProvider>
                    </StopwatchProvider>
                    <SettingsDrawer
                      canReset={settings.isCustom}
                      onClose={settings.handleDrawerClose}
                      onReset={settings.handleReset}
                      onUpdate={settings.handleUpdate}
                      open={settings.openDrawer}
                      values={{
                        colorPreset: 'indigo',
                        contrast: 'normal',
                        direction: 'ltr',
                        paletteMode: 'light',
                        responsiveFontSizes: settings.responsiveFontSizes,
                        stretch: settings.stretch,
                        layout: 'vertical',
                        navColor: 'discrete',
                      }}
                    />
                    <Toaster />
                    <DataDogRum />
                  </RTL>
                </ThemeProvider>
              );
            }}
          </SettingsConsumer>
        </SettingsProvider>
      </LocalizationProvider>
    </SplitFactoryProvider>
  ) : (
    <>{auth.login()}</>
  );
};
